import { isAction, type Middleware } from 'redux';

import { recordTriggeredHint } from '../../actions/user.actions';
import type { StoreType } from '../../reducers';
import { selectTriggeredHints, selectUser } from '../../reducers/user.selectors';
import type { AppDispatch } from '../../store';
import { isTrialReportCardAttributeEligible } from './isTrialReportCardEligible';
import { reportCardAreas } from './reportCard.config';

/**
 * Records Trial Report Card completions for eligible trial users. After an
 * action passes through, if it marks one of the three areas complete (matched
 * via each area's `completionActionType` plus its optional `matches` predicate)
 * and the user is attribute-eligible, the area's hint is persisted via the
 * server-backed `recordTriggeredHint` (WOR-795). No-ops when the user is
 * unloaded/ineligible or the hint is already recorded; `recordTriggeredHint`
 * is itself optimistic and idempotent, so the guard here just avoids the
 * redundant dispatch.
 */
export const trialReportCardMiddleware: Middleware<unknown, StoreType, AppDispatch> =
	(store) => (next) => (action) => {
		const result = next(action);

		if (!isAction(action)) {
			return result;
		}

		const completedArea = reportCardAreas.find(
			(area) => area.completionActionType === action.type && (area.matches?.(action) ?? true)
		);
		if (!completedArea) {
			return result;
		}

		const state = store.getState();
		if (!isTrialReportCardAttributeEligible(selectUser(state))) {
			return result;
		}
		if (selectTriggeredHints(state).includes(completedArea.hintKey)) {
			return result;
		}

		store.dispatch(recordTriggeredHint(completedArea.hintKey));

		return result;
	};
